import * as React from "react";
import { CalendarIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { hu } from "date-fns/locale";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const HU_MONTHS = [
  "Január", "Február", "Március", "Április", "Május", "Június",
  "Július", "Augusztus", "Szeptember", "Október", "November", "December",
];

function CalendarHeader({ displayMonth, fromYear, toYear, onChange }) {
  const month = displayMonth.getMonth();
  const year = displayMonth.getFullYear();
  const goto = (m, y) => {
    const next = new Date(displayMonth);
    next.setMonth(m);
    next.setFullYear(y);
    next.setDate(1);
    onChange(next);
  };
  const years = React.useMemo(() => {
    const arr = [];
    for (let y = toYear; y >= fromYear; y -= 1) arr.push(y);
    return arr;
  }, [fromYear, toYear]);
  return (
    <div className="mb-3 flex items-center justify-between gap-2 px-1">
      <button
        type="button"
        onClick={() => goto(month === 0 ? 11 : month - 1, month === 0 ? year - 1 : year)}
        className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
        aria-label="Előző hónap"
      >
        <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
      <div className="flex flex-1 items-center justify-center gap-1.5">
        <div className="relative">
          <select
            value={month}
            onChange={(e) => goto(parseInt(e.target.value, 10), year)}
            className="appearance-none rounded-md bg-transparent px-2 py-1 pr-5 text-[13px] font-medium tracking-tight outline-none transition-colors hover:bg-foreground/[0.06] focus:bg-foreground/[0.06]"
          >
            {HU_MONTHS.map((label, idx) => (
              <option key={label} value={idx}>{label}</option>
            ))}
          </select>
          <ChevronRight className="pointer-events-none absolute right-1 top-1/2 h-3 w-3 -translate-y-1/2 rotate-90 text-muted-foreground/70" />
        </div>
        <div className="relative">
          <select
            value={year}
            onChange={(e) => goto(month, parseInt(e.target.value, 10))}
            className="appearance-none rounded-md bg-transparent px-2 py-1 pr-5 text-[13px] font-medium tabular-nums tracking-tight outline-none transition-colors hover:bg-foreground/[0.06] focus:bg-foreground/[0.06]"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <ChevronRight className="pointer-events-none absolute right-1 top-1/2 h-3 w-3 -translate-y-1/2 rotate-90 text-muted-foreground/70" />
        </div>
      </div>
      <button
        type="button"
        onClick={() => goto(month === 11 ? 0 : month + 1, month === 11 ? year + 1 : year)}
        className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
        aria-label="Következő hónap"
      >
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

function StyledCalendar({ selected, onSelect, locale, fromYear, toYear, disabled }) {
  const [displayMonth, setDisplayMonth] = React.useState(selected || new Date());
  React.useEffect(() => {
    if (selected) setDisplayMonth(selected);
  }, [selected]);
  return (
    <div className="p-3">
      <CalendarHeader
        displayMonth={displayMonth}
        fromYear={fromYear}
        toYear={toYear}
        onChange={setDisplayMonth}
      />
      <DayPicker
        mode="single"
        month={displayMonth}
        onMonthChange={setDisplayMonth}
        selected={selected}
        onSelect={onSelect}
        locale={locale}
        weekStartsOn={1}
        disabled={disabled}
        showOutsideDays
        classNames={{
          months: "",
          month: "",
          caption: "hidden",
          nav: "hidden",
          table: "w-full border-collapse",
          head_row: "flex",
          head_cell:
            "w-9 text-center text-[10.5px] font-medium uppercase tracking-[0.12em] text-muted-foreground/70",
          row: "mt-1 flex w-full",
          cell:
            "relative h-9 w-9 p-0 text-center text-[13px] focus-within:relative focus-within:z-20",
          day:
            "inline-flex h-9 w-9 items-center justify-center rounded-full font-normal tabular-nums transition-colors hover:bg-foreground/[0.06] focus:outline-none focus-visible:ring-1 focus-visible:ring-ring aria-selected:font-semibold",
          day_selected:
            "!bg-foreground !text-background hover:!bg-foreground hover:!text-background focus:!bg-foreground focus:!text-background",
          day_today:
            "ring-1 ring-foreground/15 ring-offset-0",
          day_outside: "text-muted-foreground/35",
          day_disabled: "text-muted-foreground/30 opacity-60",
          day_hidden: "invisible",
        }}
      />
    </div>
  );
}

/**
 * Browser-agnostic date input that replaces `<input type="date">`.
 * - Value is an ISO YYYY-MM-DD string ("" for empty).
 * - Avoids native browser pickers (Safari AutoFill, iOS wheel quirks, etc.).
 *
 * Props:
 *   value: string (YYYY-MM-DD or "")
 *   onChange: (next: string) => void
 *   disabled?: boolean
 *   placeholder?: string
 *   className?: string
 *   "data-testid"?: string
 *   id?: string
 *   minDate?: Date
 *   maxDate?: Date
 *   clearable?: boolean  (default true)
 */
export const DateInput = React.forwardRef(function DateInput(
  {
    value,
    onChange,
    disabled,
    placeholder = "Válassz dátumot",
    className,
    minDate,
    maxDate,
    clearable = true,
    id,
    fromYear = 1940,
    toYear = 2040,
    ...rest
  },
  ref
) {
  const [open, setOpen] = React.useState(false);

  const parsed = React.useMemo(() => {
    if (!value || typeof value !== "string") return null;
    const d = parse(value, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : null;
  }, [value]);

  const handleSelect = (next) => {
    if (!next) {
      onChange("");
      setOpen(false);
      return;
    }
    onChange(format(next, "yyyy-MM-dd"));
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  const testId = rest["data-testid"];

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          ref={ref}
          type="button"
          id={id}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "group flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            !parsed && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate text-left tabular-nums">
            {parsed ? format(parsed, "yyyy. MM. dd.") : placeholder}
          </span>
          <span className="ml-2 flex shrink-0 items-center gap-1 text-muted-foreground">
            {clearable && parsed && !disabled && (
              <span
                role="button"
                aria-label="Dátum törlése"
                onClick={handleClear}
                className="rounded p-0.5 hover:bg-foreground/[0.06] hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <CalendarIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-auto rounded-xl border border-border/80 bg-popover p-0 shadow-lg"
      >
        <StyledCalendar
          selected={parsed || undefined}
          onSelect={handleSelect}
          locale={hu}
          fromYear={fromYear}
          toYear={toYear}
          disabled={(d) => {
            if (minDate && d < minDate) return true;
            if (maxDate && d > maxDate) return true;
            return false;
          }}
        />
        <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">
          <button
            type="button"
            onClick={() => handleSelect(new Date())}
            className="rounded-md px-2 py-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
          >
            Ma
          </button>
          {clearable && parsed && (
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="rounded-md px-2 py-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
            >
              Törlés
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
});
